import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { App } from './App';
import { msalConfig } from './config/msal.config';
import { AUTH_API_CONFIG } from './config/api.config';
import { initializeMsalAuth } from './services/authInitializer.service';
import { msalReady } from './services/msalBootstrap.service';
import { setupApiInterceptor } from './services/api.service';
import { shouldUseMsalRedirect } from './utils/featureFlags.utils';
import { cleanupRedirectState } from './utils/redirectTransaction.utils';
import './styles/zalando-fonts.css';
import './index.css';

// MSAL initialization is now handled by msalBootstrap.service.ts
// The bootstrap service creates the instance and handles initialization
// All MSAL operations should await msalReady before proceeding

// Get or create root - prevents duplicate root creation during HMR
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Store root in module scope to prevent duplicate creation during HMR
// During HMR, modules are re-evaluated, so we check if root already exists
let root: ReactDOM.Root;

// Type for window with React root property
type WindowWithReactRoot = Window & { __reactRoot?: ReactDOM.Root };

// Check if we've already created a root (for HMR support)
// Use a global variable to persist across HMR
const windowWithRoot = window as WindowWithReactRoot;
if (!windowWithRoot.__reactRoot) {
  root = ReactDOM.createRoot(rootElement);
  windowWithRoot.__reactRoot = root;
} else {
  // We know __reactRoot exists here because of the if check above
  root = windowWithRoot.__reactRoot;
}

// Bootstrap and render app
(async () => {
  // Initialize Application Insights early (only in production)
  // This is a recommendation from CODE_REVIEW_CONTEXT7.md (Section 10.2.2)
  // Use dynamic import to allow Vite to code-split this module
  if (import.meta.env.PROD) {
    try {
      const { initializeApplicationInsights } = await import('./services/applicationInsights.service');
      initializeApplicationInsights();
      if (import.meta.env.DEV) {
        console.log('[Main] Application Insights initialization started');
      }
    } catch (error) {
      // Don't block app startup if Application Insights fails
      console.warn('[Main] Failed to initialize Application Insights:', error);
    }
  }

  if (AUTH_API_CONFIG.providerType === 'msal') {
    // Phase 9 (Item 10): Cleanup redirect state on app start if redirect is disabled
    // This ensures clean state for rollback
    const useRedirect = shouldUseMsalRedirect();
    if (!useRedirect) {
      console.log('[Main] Redirect flow disabled, cleaning up redirect state (rollback hygiene)');
      cleanupRedirectState();
    }
    
    // Wait for MSAL bootstrap to complete before rendering
    // This ensures active account is set before route guards run
    try {
      const instance = await msalReady;
      
      if (instance) {
        // Configure MSAL auth provider with feature flag support
        initializeMsalAuth(instance, useRedirect ? InteractionType.Redirect : InteractionType.Popup);
        // REQUIRED: Attach auth interceptors before first render. Do not remove.
        // Interceptors are only attached here at bootstrap; MsalRedirectHandler/AccountSetup only update
        // the token provider ref. Without this call, direct navigation or refresh to protected routes
        // (e.g. /form/1) would send requests without a Bearer token and the backend returns 401.
        setupApiInterceptor();
        // Now render React app (bootstrap is complete, active account is set)
        const AppWrapper = import.meta.env.DEV ? React.Fragment : React.StrictMode;
        root.render(
          <AppWrapper>
            <MsalProvider instance={instance}>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <App />
              </BrowserRouter>
            </MsalProvider>
          </AppWrapper>
        );
      } else {
        // MSAL initialization failed - render app without MSAL
        const AppWrapper = import.meta.env.DEV ? React.Fragment : React.StrictMode;
        root.render(
          <AppWrapper>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <App />
            </BrowserRouter>
          </AppWrapper>
        );
        console.error('[MSAL] Initialization failed. App rendered without MSAL support.');
      }
    } catch (error) {
      console.error('[MSAL] Bootstrap error:', error);
      
      // Check for endpoint resolution errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('endpoints_resolution_error') || errorMessage.includes('ERR_NAME_NOT_RESOLVED') || errorMessage.includes('CORS')) {
        const baseAuthority = msalConfig.auth.authority?.split('/').slice(0, 3).join('/') || '';
        
        console.error(
          '\n❌ MSAL Endpoint Resolution / CORS Error\n' +
          'The authority URL cannot be resolved or is blocked by CORS policy.\n\n' +
          '⚠️ IMPORTANT: CORS errors for MSAL endpoint discovery are SERVER-SIDE issues.\n' +
          'They cannot be fixed with a client-side CORS module.\n\n' +
          'Possible causes:\n' +
          '1. The authority URL is incorrect or the domain doesn\'t exist\n' +
          '2. The External ID tenant is not fully provisioned\n' +
          '3. The domain is not accessible from your network\n' +
          '4. DNS resolution is failing\n\n' +
          'To fix this:\n' +
          '1. Verify your authority URL in Azure Portal:\n' +
          '   - Go to Azure Portal -> App registrations -> Your app -> Endpoints\n' +
          '   - Copy the EXACT authority URL shown there\n' +
          '   - Update VITE_AZURE_AUTHORITY in .env.local\n\n' +
          '2. Test the authority URL accessibility:\n' +
          `   - Try opening in browser: ${baseAuthority}/.well-known/openid-configuration\n` +
          '   - If it doesn\'t load, the domain is incorrect or not accessible\n\n' +
          '3. Verify External ID tenant is fully provisioned:\n' +
          '   - Check Azure Portal -> External ID tenant status\n' +
          '   - Ensure all required services are enabled\n\n' +
          `Current authority: ${msalConfig.auth.authority}\n` +
          `Base authority: ${baseAuthority}\n`
        );
        
        // Test authority accessibility in development
        if (import.meta.env.DEV && baseAuthority) {
          try {
            const { testAuthorityAccessibility } = await import('./utils/corsHelper.utils');
            const testResult = await testAuthorityAccessibility(baseAuthority);
            console.log('[MSAL] Authority accessibility test:', testResult);
          } catch (testError) {
            console.warn('[MSAL] Could not test authority accessibility:', testError);
          }
        }
      }
      
      // Render app without MSAL provider if initialization fails
      const AppWrapper = import.meta.env.DEV ? React.Fragment : React.StrictMode;
      root.render(
        <AppWrapper>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <App />
          </BrowserRouter>
        </AppWrapper>
      );
    }
  } else {
    // Non-MSAL auth - render immediately
    const AppWrapper = import.meta.env.DEV ? React.Fragment : React.StrictMode;
    root.render(
      <AppWrapper>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </BrowserRouter>
      </AppWrapper>
    );
  }
})();

